import { useMemo, type FC } from 'react';
import { useAtomValue } from 'jotai';
import { Source } from 'react-map-gl';
import type { Expression } from 'mapbox-gl';
import { Layer, LayerBand, LAYER_ORDER } from 'components/map';
import { uwiTileUrls } from 'components/map/uwi-tiles';
import { selectedWellUwiAtom, wellStatusColorsAtom } from './atoms';
import { PAINTED_WELL_STATUSES } from './well-statuses';

// A wide, blurred glow drawn under the bore, matching the mock's `wells-highlight`
// layer — 8px at 0.85 opacity with a 4px blur, in the well's own status colour.
const HIGHLIGHT_WIDTH = 8;
const HIGHLIGHT_OPACITY = 0.85;
const HIGHLIGHT_BLUR = 4;
/** Mapbox `match` needs a default for a status the legend doesn't name. */
const FALLBACK_COLOR = '#0F62FE';

// Paints the selected well's halo in whatever colour the legend gives its status, so
// the highlight reads as that well rather than as a separate thing.
const buildColorExpr = (statusColors: Record<string, string>): Expression => {
  const flat: string[] = [];
  PAINTED_WELL_STATUSES.forEach((status) => {
    flat.push(status.value, statusColors[status.value] ?? status.color);
  });
  // Downcased before matching: these tiles carry the status uppercase ('DUC'), while
  // the legend keys on the lowercase value the wells query returns.
  return [
    'match',
    ['downcase', ['coalesce', ['get', 'status'], '']],
    ...flat,
    FALLBACK_COLOR,
  ] as Expression;
};

// The halo under the well selected in the table. Tiles only that UWI, so nothing is
// fetched until a row is picked, and the layer goes when the selection clears.
export const AreaHighlightedWellLayer: FC = () => {
  const uwi = useAtomValue(selectedWellUwiAtom);
  const statusColors = useAtomValue(wellStatusColorsAtom);

  const colorExpr = useMemo(() => buildColorExpr(statusColors), [statusColors]);

  const tileUrl = useMemo(() => (uwi ? uwiTileUrls([uwi])[0] : null), [uwi]);

  if (!tileUrl) {
    return null;
  }

  return (
    // Fixed source ids: react-map-gl asserts on a changed id, and a stable one lets it
    // hand the new tile URL to the live source via `setTiles`.
    <LayerBand band={LAYER_ORDER.areaWells}>
      {/* Survey line, for a well that has one. */}
      <Source id="area-well-highlight-survey" type="vector" tiles={[tileUrl]}>
        <Layer
          id="area-well-highlight-survey-line"
          type="line"
          source-layer="hits"
          layout={{ 'line-join': 'round', 'line-cap': 'round' }}
          paint={{
            'line-color': colorExpr,
            'line-width': HIGHLIGHT_WIDTH,
            'line-opacity': HIGHLIGHT_OPACITY,
            'line-blur': HIGHLIGHT_BLUR,
          }}
        />
      </Source>

      {/* Stick fallback: surface → bottom hole, for a well with no survey. */}
      <Source
        id="area-well-highlight-stick"
        type="vector"
        tiles={[tileUrl.replace('/survey/', '/stick/')]}
      >
        <Layer
          id="area-well-highlight-stick-line"
          type="line"
          source-layer="hits"
          layout={{ 'line-join': 'round', 'line-cap': 'round' }}
          paint={{
            'line-color': colorExpr,
            'line-width': HIGHLIGHT_WIDTH,
            'line-opacity': HIGHLIGHT_OPACITY,
            'line-blur': HIGHLIGHT_BLUR,
          }}
        />
      </Source>
    </LayerBand>
  );
};
